import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { GuideService } from '../pages/guide/services/guide.service';
import { UserService } from '../../../core/services/user.service';
import { ClaimsService } from 'src/app/core/services/claims.service';
import { Claim } from 'src/app/core/models/claim';

@Injectable({
  providedIn: 'root',
})
export class GuideGuardService {
  constructor(
    private router: Router,
    private guideService: GuideService,
    private userService: UserService,
    private claimService: ClaimsService
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
    console.log('[GUIDE_GUARD] Checking if user can access route', {
      url: state.url,
      route: route.routeConfig?.path,
    });

    // ✅ Check if user has guideComplete flag in PII
    const explicitCaseRedirect = localStorage.getItem('postLoginRedirectBypassGuide');
    if (
      explicitCaseRedirect &&
      state.url === explicitCaseRedirect &&
      route.routeConfig?.path === 'cases/:id'
    ) {
      localStorage.removeItem('postLoginRedirectBypassGuide');
      console.log('[GUIDE_GUARD] Explicit authenticated case redirect, allowing case page', {
        url: state.url,
      });
      return true;
    }

    const user = await this.userService.getCurrentUser();
    let piiAttributes: any = {};
    let userClaims: Claim[] = [];

    if (user?.id) {
      try {
        const userWithPii = await this.userService.getWithPII(user.id);
        piiAttributes = userWithPii?.pii?.attributes || {};
      } catch (error) {
        console.warn('[GUIDE_GUARD] Failed to check PII attributes', error);
      }

      try {
        userClaims = await this.claimService.getAllClaimsForUserId(user.id);
      } catch (error) {
        console.warn('[GUIDE_GUARD] Failed to check User Claims', error);
      }

      // ✅ guideComplete é uma flag GLOBAL do usuário no PII — só é um atalho
      // seguro quando ele tem uma única claim. Pra multi-claim, aplicá-la
      // direto deixaria o guide vazando pra qualquer claim nova com actions
      // pendentes (ex: Contrato) só porque uma claim anterior já foi concluída
      // (regressão real observada no CPK-3911 — o check por-claim abaixo nunca
      // era alcançado porque esse retorno antecipado rodava primeiro).
      if (piiAttributes.guideComplete && userClaims.length <= 1) {
        console.log(
          '[GUIDE_GUARD] User has guideComplete flag in PII, allowing access'
        );
        return true;
      }

      // ✅ Se user possuir mais de uma claim, o lock do guide passa a ser POR
      // CLAIM (não mais um bypass global) — evita que o claim de destino
      // ainda pendente (ex: KYC de um novo case) fique acessível fora do
      // guide só porque o usuário tem outras claims já resolvidas (CPK-3911).
      if (userClaims.length > 1) {
        const targetClaimId = this.getTargetClaimId(route);

        if (!targetClaimId) {
          // Acabou de concluir o guide de OUTRO claim agora (redirect do
          // guide.component.ts) - deixa a celebração/home renderizar antes de
          // forçar qualquer outro claim pendente (CPK-4171).
          if (route.queryParamMap.get('guideCompleted') === 'true') {
            console.log(
              '[GUIDE_GUARD] guideCompleted redirect in progress, allowing home to render celebration'
            );
            return true;
          }

          // Rota genérica (home, cases, profile, news, etc.) sem claim explícito
          // na URL - resolve o claim não finalizado mais recente e força o
          // guide dele quando ainda pendente (CPK-4171).
          const unfinishedClaim = await this.claimService.getMyMostRecentUnfinishedClaim();

          if (!unfinishedClaim) {
            console.log(
              '[GUIDE_GUARD] Multi-claim user, no unfinished claim, allowing free navigation'
            );
            return true;
          }

          const mustAccessUnfinished = await this.guideService.claimRequiresGuide(
            unfinishedClaim.id
          );

          if (!mustAccessUnfinished) {
            console.log(
              '[GUIDE_GUARD] Multi-claim user, most recent unfinished claim has no pending guide requirements',
              { claimId: unfinishedClaim.id }
            );
            return true;
          }

          console.log(
            '[GUIDE_GUARD] Multi-claim user on generic route, redirecting to most recent unfinished claim',
            { claimId: unfinishedClaim.id }
          );
          this.router.navigateByUrl(`client/claim/${unfinishedClaim.id}/guide`);
          return false;
        }

        const mustAccessTargetClaim = await this.guideService.claimRequiresGuide(
          targetClaimId
        );

        if (!mustAccessTargetClaim) {
          console.log(
            '[GUIDE_GUARD] Multi-claim user, target claim has no pending guide requirements',
            { targetClaimId }
          );
          return true;
        }

        console.log(
          '[GUIDE_GUARD] Multi-claim user, target claim still requires guide, redirecting',
          { targetClaimId }
        );
        this.router.navigateByUrl(`client/claim/${targetClaimId}/guide`);
        return false;
      }
    }

    // ALWAYS refresh claim to get latest status (especially INACTIVE check)
    const claim = await this.guideService.refreshMainClaim();

    if (!claim) {
      console.log('[GUIDE_GUARD] No claim found, allowing access');
      return true;
    }

    // Check if claim is INACTIVE - if so, don't force guide access
    if (claim.status === 'INACTIVE') {
      console.log('[GUIDE_GUARD] Claim is INACTIVE, allowing free navigation', {
        claimId: claim.id,
        status: claim.status,
        targetUrl: state.url,
      });
      return true;
    }

    // ✅ Check APENAS se o wizard está habilitado - NÃO carrega actions aqui
    const isWizardEnabled = !!claim.cp_case?.configuration?.enable_actions_wizard;
    
    if (!isWizardEnabled) {
      console.log('[GUIDE_GUARD] Wizard not enabled, allowing free navigation', {
        claimId: claim.id,
        caseId: claim.cp_case?.id,
      });
      return true;
    }

    // ✅ Se skipCheck está ativo, permite navegação livre
    if (this.guideService['skipCheck']) {
      console.log('[GUIDE_GUARD] Skip check active, allowing access');
      return true;
    }

    // ✅ Verifica se o usuário DEVE acessar o guide (tem actions pendentes)
    const mustAccess = await this.guideService.checkIfUserMustAccessGuide();

    if (!mustAccess) {
      console.log(
        '[GUIDE_GUARD] Wizard enabled but all actions complete, allowing access'
      );

      // ✅ Se todas as ações estiverem completas, marca flag no PII
      if (user?.id && !piiAttributes.guideComplete) {
        console.log(
          '[GUIDE_GUARD] All actions complete, setting guideComplete flag in PII'
        );
        try {
          await this.userService.updateProfile({
            pii: {
              attributes: {
                ...piiAttributes,
                guideComplete: true,
              },
            },
          });
        } catch (e) {
          console.error(
            '[GUIDE_GUARD] Failed to set guideComplete flag in PII',
            e
          );
        }
      }

      return true;
    }

    // ✅ Se já está tentando acessar o guide, permite (evita loop de redirecionamento)
    if (state.url.includes('/guide')) {
      console.log('[GUIDE_GUARD] Already accessing guide, allowing access', {
        url: state.url,
      });
      return true;
    }

    // ✅ REDIRECIONA IMEDIATAMENTE para o guide
    console.log('[GUIDE_GUARD] Wizard enabled and pending actions, redirecting to guide', {
      from: state.url,
      to: `client/claim/${claim.id}/guide`,
    });
    
    this.router.navigateByUrl(`client/claim/${claim.id}/guide`);
    return false;
  }

  /**
   * Extrai o claimId da rota de destino, quando a rota é de fato específica
   * de uma claim (evita confundir com o :id de 'cases/:id', que é um caseId).
   */
  private getTargetClaimId(route: ActivatedRouteSnapshot): string | null {
    switch (route.routeConfig?.path) {
      case 'claim/:id':
      case 'claim/:cid/q/:qcode':
        return route.paramMap.get('id') || route.paramMap.get('cid');
      case 'messages/:claim':
      case 'messages/new/:claim':
        return route.paramMap.get('claim');
      default:
        return null;
    }
  }
}
